import { useEffect, useEffectEvent } from 'react';

/**
 * Calls `onRestore` each time the browser restores this page from its back/forward cache: a
 * `pageshow` event with `persisted: true`. An ordinary `pageshow`, the first load included, is not a
 * restore and calls nothing.
 *
 * A restored page comes back with its React state as it was frozen, so state that described a moment
 * the user has moved past (a busy button whose redirect has already happened, a one-time message the
 * user has already seen) comes back too, and the URL that would have told the page otherwise has
 * already been cleaned (`.forge/lessons.md` L-109). Each caller decides what to reset.
 *
 * The listener is added once and removed on unmount; `onRestore` is read when the event fires, so the
 * caller need not keep it stable.
 */
export function usePageRestore(onRestore: () => void): void {
    const restore = useEffectEvent(onRestore);

    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                restore();
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);
}
